/**
 * Decodes the payload (second segment) of a JWT without verifying its
 * signature. The frontend never trusts this for authorization — the backend
 * remains the gate (COR-545); this only reads the `role` claim the app already
 * holds so the UI can mirror the caller's tier.
 *
 * Returns the parsed payload object, or `undefined` for any malformed input
 * (missing segment, invalid base64url, or non-object JSON). No dependency is
 * added — a JWT payload is just base64url-encoded JSON.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | undefined {
  const payloadSegment = token.split('.')[1];
  if (!payloadSegment) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(base64UrlDecode(payloadSegment));
    if (typeof parsed !== 'object' || parsed === null) {
      return undefined;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
